import React, { useState } from 'react';

import CardDialog from '~/components/CardDialog';
import Button from '~/components/core/Atomic/Buttons/Button';
import EmptyState from '~/components/core/EmptyState';
import SkeletonTable from '~/components/core/Skeletons/SkeletonTable';
import Text from '~/components/core/TextComponents/Text';
import { AddIcon } from '~/components/deprecatedMuiIcons';

import { useN8nConfiguration } from './hooks/useN8nConfiguration';
import type { N8nConfigurationFormValues } from './utils/validation';
import N8nConfigurationCard from './N8nConfigurationCard';
import N8nConfigurationDialog from './N8nConfigurationDialog';

const N8nConfigurationTab: React.FC = () => {
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const {
    isLoading,
    isError,
    configuration,
    hasConfiguration,
    createConfiguration,
    updateConfiguration,
    isSubmitting,
  } = useN8nConfiguration();

  const handleCreateClick = () => {
    setIsDialogOpen(true);
  };

  const handleEditClick = () => {
    setIsDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setIsDialogOpen(false);
  };

  const handleSubmit = async (values: N8nConfigurationFormValues) => {
    try {
      if (hasConfiguration) {
        await updateConfiguration(values);
      } else {
        await createConfiguration(values);
      }
      handleCloseDialog();
    } catch (error) {
      // Error already handled in hook
    }
  };

  if (isLoading || isError) {
    return <SkeletonTable rowsCount={5} columnsCount={3} isError={isError} />;
  }

  return (
    <CardDialog title="N8N CONFIGURATION">
      <Text variant={Text.VARIANTS.SM} weight={Text.WEIGHTS.REGULAR} colorVariant={Text.COLOR_VARIANTS.PRIMARY}>
        Configure n8n workflow engine integration to enable automated workflow execution for your organization.
      </Text>

      {!hasConfiguration && (
        <div className="flex justify-end">
          <Button color="primary" onClick={handleCreateClick} disabled={isSubmitting}>
            <AddIcon />
            Create Configuration
          </Button>
        </div>
      )}

      {hasConfiguration && configuration ? (
        <N8nConfigurationCard configuration={configuration} onEdit={handleEditClick} isLoading={isSubmitting} />
      ) : (
        <EmptyState
          title="No N8n Configuration"
          subtitle="Create a configuration to enable n8n workflow engine integration for automated workflow execution."
        />
      )}

      <N8nConfigurationDialog
        isOpen={isDialogOpen}
        configuration={configuration === null ? undefined : configuration}
        onSubmit={handleSubmit}
        onClose={handleCloseDialog}
        isEdit={hasConfiguration}
      />
    </CardDialog>
  );
};

export default N8nConfigurationTab;
